"use client";

import {
  EdgenModalCreateAble,
  type EdgenModalCreateAbleProps,
} from "@components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@components/EdgenModal/waitForEdgenModalRoot";
import { PlaidConnectFlowModal } from "./PlaidConnectFlowModal";
import type { PlaidConnectFlowOptions } from "./types";
import { IS_PLAID_OPEN } from "@/src/components/PlaidImportModal/openPlaidImportModal";
import { OspToast } from "@ospweb/sdk-ui";

let pendingPlaidConnectFlowModal: Promise<void> | null = null;

export const isPlaidConnectFlowAvailable = () => IS_PLAID_OPEN;

/**
 * Plaid flow 的步骤尺寸会变化：常规步骤为 PC 539×min(852, 90vh) / H5
 * fullscreen，退出挽留则是 PC 480px 居中卡片 / H5 bottom sheet。
 * 壳层保持透明并由步骤自身决定尺寸，避免在退出步骤外再套一层固定大弹窗。
 */
const plaidConnectFlowShellProps = (): Pick<
  EdgenModalCreateAbleProps,
  "className" | "contentStyle" | "styles" | "containerStyle"
> => ({
  className: "!m-0 !max-w-none !p-0",
  contentStyle: {
    width: "fit-content",
    height: "auto",
    maxWidth: "100vw",
    maxHeight: "100dvh",
    overflow: "visible",
    borderRadius: 0,
    background: "transparent",
    boxShadow: "none",
    padding: 0,
  },
  styles: {
    body: {
      height: "auto",
    },
  },
  containerStyle: {
    width: "fit-content",
    height: "fit-content",
    maxWidth: "100vw",
    maxHeight: "100dvh",
    overflow: "visible",
  },
});

export const openPlaidConnectFlowModal = (
  options: PlaidConnectFlowOptions = {},
): Promise<void> => {
  if (!IS_PLAID_OPEN) {
    OspToast.info({
      content: "Coming soon, please choose other ways",
    });
    return Promise.resolve();
  }

  if (pendingPlaidConnectFlowModal) {
    return pendingPlaidConnectFlowModal;
  }

  pendingPlaidConnectFlowModal = callEdgenModalWhenRootReadyAsync(
    () =>
      new Promise<void>((resolve) => {
        let settled = false;
        let modalInstance: Promise<boolean> | undefined;

        const finish = () => {
          if (settled) {
            return;
          }
          settled = true;
          pendingPlaidConnectFlowModal = null;
          if (!modalInstance) {
            EdgenModalCreateAble.end(false);
          } else {
            EdgenModalCreateAble.end(modalInstance, false);
          }
          options.onClose?.();
          resolve();
        };

        modalInstance = EdgenModalCreateAble.call({
          children: (
            <PlaidConnectFlowModal options={options} onCloseModal={finish} />
          ),
          ...plaidConnectFlowShellProps(),
          showClose: false,
          maskClosable: false,
          destroyOnClose: true,
        });
      }),
  ).catch((error) => {
    pendingPlaidConnectFlowModal = null;
    throw error;
  });

  return pendingPlaidConnectFlowModal;
};
